import type {
	HapticImpactStyle,
	HapticNotificationType,
	HapticsPort,
} from "../../ports/haptics.port";

// Тактильная отдача в Capacitor.
//
// Доступ — canonical `window.Capacitor.Plugins.*`, а НЕ `import("@capacitor/haptics")`:
// bare specifier не резолвится в production WebView (см. capacitor-plugins.ts,
// rules/capacitor-platform.md). Побочно это убирает зависимость пакета от
// @capacitor/haptics в графе web-сборки — адаптер можно импортировать статически
// и выбирать в рантайме.
//
// Два источника, по убыванию качества:
//   1. StudyHubHaptics — наш нативный плагин (Android). VibrationEffect.Composition
//      (API 30+) + ротация AudioAttributes.usage против Samsung scaler throttle.
//      Раньше он был написан, зарегистрирован в MainActivity — и не вызывался
//      ниоткуда, потому что адаптер шёл в стандартный плагин.
//   2. Haptics — стандартный @capacitor/haptics (iOS Taptic Engine; на Android —
//      грубее нашего). Enum'ы плагина = строки в верхнем регистре, поэтому
//      canonical-вызов передаёт их напрямую.
// Если ни одного нет (web/PWA) — вызывающий получает web-адаптер (см. use-haptic).

interface StudyHubHapticsBridge {
	impact(opts: { style: HapticImpactStyle; strength?: number }): Promise<void> | void;
	selection(): Promise<void> | void;
	notification(opts: { type: HapticNotificationType }): Promise<void> | void;
}

interface StandardHapticsBridge {
	impact(opts: { style: "LIGHT" | "MEDIUM" | "HEAVY" }): Promise<void> | void;
	selectionStart(): Promise<void> | void;
	selectionEnd(): Promise<void> | void;
	notification(opts: { type: "SUCCESS" | "WARNING" | "ERROR" }): Promise<void> | void;
}

const IMPACT_MAP: Record<HapticImpactStyle, "LIGHT" | "MEDIUM" | "HEAVY"> = {
	light: "LIGHT",
	medium: "MEDIUM",
	heavy: "HEAVY",
};

const NOTIFICATION_MAP: Record<HapticNotificationType, "SUCCESS" | "WARNING" | "ERROR"> = {
	success: "SUCCESS",
	warning: "WARNING",
	error: "ERROR",
};

function getPlugin<T>(name: string): T | null {
	if (typeof window === "undefined") return null;
	const plugins = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })
		.Capacitor?.Plugins;
	return (plugins?.[name] as T | undefined) ?? null;
}

const custom = (): StudyHubHapticsBridge | null =>
	getPlugin<StudyHubHapticsBridge>("StudyHubHaptics");
const standard = (): StandardHapticsBridge | null => getPlugin<StandardHapticsBridge>("Haptics");

export const hapticsCapacitorAdapter: HapticsPort = {
	async impact({ style }) {
		const own = custom();
		if (own) {
			await own.impact({ style });
			return;
		}
		await standard()?.impact({ style: IMPACT_MAP[style] });
	},
	async selection() {
		const own = custom();
		if (own) {
			await own.selection();
			return;
		}
		const std = standard();
		if (!std) return;
		await std.selectionStart();
		await std.selectionEnd();
	},
	async notification({ type }) {
		const own = custom();
		if (own) {
			await own.notification({ type });
			return;
		}
		await standard()?.notification({ type: NOTIFICATION_MAP[type] });
	},
};
